/**
 * @file
 * @fileOverview This file contains the CustomButton class for the Phaser scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import "phaser"
/**
 * CustomButton class extending Phaser.GameObjects.Container
 * @class
 * @extends Phaser.GameObjects.Container
 * @classdesc A customizable button class for Phaser games with text, icon, and various interactive features.
 */
class CustomButton extends Phaser.GameObjects.Container {
  /**
   * Creates a new CustomButton instance.
   * @constructor
   * @param {Phaser.Scene} scene - The Scene to which this button belongs.
   * @param {string} id - Unique identifier for the button.
   * @param {number} x - The x position of the button.
   * @param {number} y - The y position of the button.
   * @param {string} text - The text to display on the button.
   * @param {string} [iconUrl] - URL of the icon image to display on the button.
   * @param {Function} callback - Function to call when the button is clicked.
   * @param {Object} [options={}] - Additional options for customizing the button.
   * @param {number} [options.width=150] - Width of the button.
   * @param {number} [options.height=50] - Height of the button.
   * @param {number} [options.bgColor=0x4caf50] - Background color of the button.
   * @param {string} [options.textColor='#ffffff'] - Color of the button text.
   * @param {string} [options.fontSize='20px'] - Font size of the button text.
   * @param {Object} [data={}] - Additional data to associate with the button.
   */
  constructor(
    scene,
    id,
    x,
    y,
    text,
    iconUrl,
    callback,
    options = {},
    data = {}
  ) {
    super(scene, x, y);
    this.id = id;
    this.data = data;
    // Default options
    this.options = {
      width: options.width || 150,
      height: options.height || 50,
      bgColor: options.bgColor || 0x4caf50,
      textColor: options.textColor || '#ffffff',
      fontSize: options.fontSize || '20px',
      ...options,
    };

    // Background
    this.background = scene.add.rectangle(
      0,
      0,
      this.options.width,
      this.options.height,
      this.options.bgColor
    );
    this.background.setOrigin(0.5);

    // Text
    this.text = scene.add
      .text(0, 0, text, {
        fontSize: this.options.fontSize,
        color: this.options.textColor,
      })
      .setOrigin(0.5);

    // Icon
    if (iconUrl) {
      scene.load.image(this.id, iconUrl);
      scene.load.once('complete', () => {
        this.icon = scene.add
          .image(0, 0, this.id)
          .setDisplaySize(this.options.width, this.options.height)
          .setOrigin(0.5);
        this.add(this.icon);
      });
      scene.load.start();
    }

    // Add to container
    this.add(this.background);
    this.add(this.text);

    // Set size
    this.setSize(this.options.width, this.options.height);

    // Enable input
    this.setInteractive(
      new Phaser.Geom.Rectangle(
        0 - this.options.width / 2,
        0 - this.options.height / 2,
        this.options.width,
        this.options.height
      ),
      Phaser.Geom.Rectangle.Contains
    )
      .on('pointerdown', () => {
        this.background.setFillStyle(0x388e3c); // Change color on click
        callback(this);
      })
      .on('pointerup', () => {
        this.background.setFillStyle(this.options.bgColor); // Reset color
      })
      .on('pointerout', () => {
        this.background.setFillStyle(this.options.bgColor); // Reset color
      });

    this.input.useHandCursor = true;
    this.input.alwaysEnabled = true;
    this.input.cursor = true;

    scene.add.existing(this);
  }
  /**
   * Get the unique identifier of the button.
   * @returns {string} The button's ID.
   */
  getId() {
    return this.id;
  }

  /**
   * Set the size of the button.
   * @param {number} width - The new width of the button.
   * @param {number} height - The new height of the button.
   */
  setSize(width, height) {
    this.options.width = width;
    this.options.height = height;
    this.background.setSize(width, height);
    if (this.icon) {
      this.icon.setX(-width / 4);
    }
  }

  /**
   * Get a data attribute associated with the button.
   * @param {string} key - The key of the data attribute to retrieve.
   * @returns {*} The value of the data attribute.
   */
  getDataAttribute(key) {
    return this.data[key];
  }

  /**
   * Set or change the icon of the button.
   * @param {string} iconKey - The key of the icon image to use.
   */
  setIcon(iconKey) {
    if (!this.icon) {
      this.icon = this.scene.add
        .image(-this.options.width / 4, 0, iconKey)
        .setScale(0.5);
      this.add(this.icon);
    } else {
      this.icon.setTexture(iconKey);
    }
  }

  /**
   * Simulate a click event on the button.
   * @param {number} x - The x coordinate of the simulated click.
   * @param {number} y - The y coordinate of the simulated click.
   */
  simulateClick(x, y) {
    if (this.contains(x, y)) {
      const pointer = this.scene.input.activePointer;

      // Create pointerdown event
      this.emit('pointerdown', pointer, this);
      // Create pointerup event
      this.emit('pointerup', pointer, this);
    }
  }
  /**
   * Check if a point is within the bounds of the button.
   * @param {number} x - The x coordinate to check.
   * @param {number} y - The y coordinate to check.
   * @returns {boolean} True if the point is within the button's bounds, false otherwise.
   */
  contains(x, y) {
    const bounds = this.getBounds();
    return (
      x >= bounds.left &&
      x <= bounds.right &&
      y >= bounds.top &&
      y <= bounds.bottom
    );
  }

  /**
   * Set new text for the button.
   * @param {string} newText - The new text to display on the button.
   */
  setText(newText) {
    this.text.setText(newText);
  }

  /**
   * Set a new background color for the button.
   * @param {number} color - The new background color (in hexadecimal format).
   */
  setBgColor(color) {
    this.options.bgColor = color;
    this.background.setFillStyle(color);
  }

  /**
   * Reset the button's background color to its default state.
   */
  resetClickStyle() {
    this.background.setFillStyle(this.options.bgColor);
  }

  /**
   * Set the visibility of the button and its child elements.
   * @param {boolean} isVisible - Whether the button should be visible or not.
   */
  setVisible(isVisible) {
    this.setVisible(isVisible);
    this.list.forEach((child) => child.setVisible(isVisible));
  }

  /**
   * Set the rotation of the button.
   * @param {number} angle - The angle of rotation (in degrees).
   */  setRotation(angle) {
    this.setAngle(angle);
  }
}

export default CustomButton;
